import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// MutationObserver: detect dialog nodes removed from the DOM. In some flows
// a dialog may be removed by a section morph before its `closeDialog`
// lifecycle runs, leaving the body locked (position: fixed/top). This
// observer performs a safe, idempotent repair when it sees a dialog node
// removed and the page shows scroll-lock signs.
try {
  const repairBodyScrollIfNeeded = () => {
    try {
      const anyOpenDialog = document.querySelector('dialog[open]');
      const bodyPos = document.body.style.position;
      const bodyTop = document.body.style.top;
      const hasScrollLock = document.documentElement.hasAttribute('scroll-lock');

      if (!anyOpenDialog && (bodyPos === 'fixed' || hasScrollLock || (bodyTop && bodyTop !== ''))) {
        const scrollY = document.body.style.top || '';
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.documentElement.removeAttribute('scroll-lock');
        if (scrollY) {
          const topNum = parseInt(scrollY, 10) * -1 || 0;
          window.scrollTo({ top: topNum, behavior: 'auto' });
        }
        console.debug('[dialog] repaired body scroll state after dialog removal');
        return true;
      }
    } catch (e) {
      // swallow
    }
    return false;
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const removed of Array.from(m.removedNodes || [])) {
        try {
          if (removed instanceof HTMLElement && removed.tagName === 'DIALOG') {
            // Run repair asynchronously to avoid blocking the mutation
            setTimeout(repairBodyScrollIfNeeded, 10);
            // Also schedule a second attempt after a short delay
            setTimeout(repairBodyScrollIfNeeded, 140);
            return;
          }
        } catch (e) {
          // ignore individual node errors
        }
      }
    }
  });

  observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
} catch (e) {
  // best-effort only
}
